import { useEffect } from 'react'

interface NotificationProps {
  readonly type: 'success' | 'error'
  readonly title: string
  readonly message: string
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly duration?: number
}

export default function Notification({
  type,
  title,
  message,
  isOpen,
  onClose,
  duration = 5000,
}: NotificationProps) {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose()
      }, duration)
      return () => clearTimeout(timer)
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, duration])

  if (!isOpen) return null

  const iconColor = type === 'success' ? 'bg-green-500' : 'bg-yellow-500'
  const icon =
    type === 'success' ? (
      <svg
        className='w-6 h-6 text-white'
        fill='none'
        stroke='currentColor'
        viewBox='0 0 24 24'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M5 13l4 4L19 7'
        />
      </svg>
    ) : (
      <svg
        className='w-6 h-6 text-white'
        fill='none'
        stroke='currentColor'
        viewBox='0 0 24 24'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
        />
      </svg>
    )

  return (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50'>
      <div className='bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative'>
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors'
        >
          <svg
            className='w-5 h-5'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        <div className='flex items-start gap-4'>
          <div
            className={`flex-shrink-0 w-12 h-12 ${iconColor} rounded-full flex items-center justify-center`}
          >
            {icon}
          </div>
          <div className='flex-1'>
            <h3 className='text-lg font-semibold text-gray-900 mb-1'>
              {title}
            </h3>
            <p className='text-gray-600 text-sm'>{message}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
